import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/programs", label: "Programs" },
  { to: "/university", label: "Online University" },
  { to: "/assistant", label: "TONIA · AI" },
  { to: "/media", label: "Media" },
  { to: "/membership", label: "Wolfkeeper+" },
  { to: "/livestream", label: "Livestream" },
  { to: "/foundation", label: "Foundation" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between h-[72px]">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo-wolfkeeper-icon.png"
            alt="Wolfkeeper crest"
            className="h-11 w-auto transition-transform group-hover:scale-105"
          />
          <div className="hidden sm:block leading-tight">
            <div className="font-serif-display text-[17px] tracking-tight">Wolfkeeper</div>
            <div className="overline text-[9px] mt-0.5">University · Est. 2002</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm text-[color:var(--wk-bone)]/85 hover:text-[color:var(--wk-bone)] transition-colors ${
                  isActive ? "text-[color:var(--wk-gold)]" : ""
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {user ? (
            <>
              <button
                data-testid="nav-dashboard"
                onClick={() => nav("/dashboard")}
                className="hidden md:inline-flex btn-ghost h-10 px-5 text-sm"
              >
                Dashboard
              </button>
              {user.is_admin && (
                <button
                  data-testid="nav-admin"
                  onClick={() => nav("/admin")}
                  className="hidden md:inline-flex text-sm text-[color:var(--wk-gold)] hover:opacity-80 link-underline"
                >
                  Studio
                </button>
              )}
              <button
                data-testid="nav-logout"
                onClick={async () => { await logout(); nav("/"); }}
                className="hidden md:inline-flex text-sm text-[color:var(--wk-bone-dim)] hover:text-[color:var(--wk-gold)] link-underline"
              >
                Sign out
              </button>
            </>
          ) : (
            <button
              data-testid="nav-signin"
              onClick={() => nav("/login")}
              className="hidden md:inline-flex text-sm text-[color:var(--wk-bone)]/85 hover:text-[color:var(--wk-gold)] link-underline"
            >
              Sign in
            </button>
          )}
          <Link
            to="/apply"
            data-testid="nav-apply"
            className="btn-gold h-10 px-5 text-sm"
          >
            Apply Now
          </Link>
          <button
            aria-label="Toggle menu"
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden p-2 text-[color:var(--wk-bone)]/85 hover:text-[color:var(--wk-gold)]"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="lg:hidden glass-strong border-t border-white/10"
          >
            <div className="px-6 py-4 grid gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.to.slice(1)}`}
                  className="py-3 text-[color:var(--wk-bone)]/90 hover:text-[color:var(--wk-gold)] font-serif-display text-xl"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="h-px bg-white/10 my-2" />
              {user ? (
                <>
                  <NavLink to="/dashboard" onClick={() => setOpen(false)} className="py-3">Dashboard</NavLink>
                  <button onClick={async () => { await logout(); setOpen(false); nav("/"); }} className="py-3 text-left">Sign out</button>
                </>
              ) : (
                <NavLink to="/login" onClick={() => setOpen(false)} className="py-3">Sign in</NavLink>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
